import GoogleServiceAbstract from '../abstracts/GoogleServiceAbstract'

export default class GoogleAdsGtag extends GoogleServiceAbstract {
  constructor(settings, CookieManager) {
    // Abstract constructor
    super(settings, CookieManager)

    if (!settings.key || !settings.identifier || !settings.gtagAw) {
      throw new Error('The settings [key, identifier, gtagAw] are required')
    }

    // config
    this.identifier = settings.identifier
    this.key = settings.key
    this.gtagAw = settings.gtagAw
    this.name = 'Google Ads (gtag.js)'
    this.group = this.getGroup('ads')
    this.uri = 'https://www.google.com/settings/ads'
    this.cookies = ['_ga', '_gtagRsqExp']
    this.cookieExpiresTimeMax = 364 * 24 * 60 * 60

    // parameters
    this.adsCookieDomain = settings.adsCookieDomain || this.CookieManager.cookieDomain
    this.gtagAwCreate = settings.gtagAwCreate
    this.adsAnonymizeIp = typeof settings.adsAnonymizeIp !== 'undefined' ? settings.adsAnonymizeIp : true
    this.adsPageView = typeof settings.adsPageView !== 'undefined' ? settings.adsPageView : true
    this.conversionLinker = typeof settings.conversionLinker !== 'undefined' ? settings.conversionLinker : false
    this.gtagMore = settings.gtagMore

    // configure remarketing data collect
    const allowAdPersonnalizationSignals = typeof settings.allowAdPersonalizationSignals !== 'undefined' ? settings.allowAdPersonalizationSignals : false
    this.gtagSet = settings.gtagSet || {}
    this.extend(this.gtagSet, {
      allow_ad_personalization_signals: allowAdPersonnalizationSignals
    })

    // check if we have info for advanced conversions
    this.formConversions = typeof settings.formConversions !== 'undefined' ? settings.formConversions : {}
    this.trackFormConversions = Object.keys(this.formConversions).length
    if (this.trackFormConversions) {
      for (const formName in this.formConversions) {
        const conversionData = this.formConversions[formName]
        const fields = conversionData.fields || {}

        // only enable advanced conversions when we have form fields
        if (Object.keys(fields).length) {
          this.extend(this.gtagSet, {
            allow_enhanced_conversions: true
          })
          break
        }
      }
    }
  }

  /**
   * Get expiring time for ads Cookie
   */
  getExpireTime() {
    const gaRsqTime = this.CookieManager.read('_gtagRsqExp')
    const d = new Date()
    let expTime
    if (!gaRsqTime) {
      this.CookieManager.create(d.getTime(), '_gtagRsqExp')
      expTime = this.cookieExpiresTimeMax
    } else {
      expTime = this.cookieExpiresTimeMax - Math.floor((d.getTime() - gaRsqTime) / 1000)
      if (expTime < 0) {
        this.CookieManager.create(d.getTime(), '_gtagRsqExp')
        expTime = this.cookieExpiresTimeMax
      }
    }

    return expTime
  }

  getUniqueIdentifier() {
    return this.gtagAw
  }

  getConsentModeStorages() {
    return [
      'ad_storage',
      'ad_user_data',
      'ad_personalization'
    ]
  }

  init() {
    // prepare base config
    const awCreate = {
      cookie_domain: this.adsCookieDomain,
      cookie_expires: this.getExpireTime()
    }

    // extra config (IP anonymization, page views, disable proprietary cookies on current domain etc.)
    this.extend(awCreate, this.gtagAwCreate || {})
    this.extend(awCreate, {
      anonymize_ip: this.adsAnonymizeIp,
      send_page_view: this.adsPageView,
      conversion_linker: this.conversionLinker
    })

    // all options are configured: place tracking
    gtag('config', this.getUniqueIdentifier(), awCreate)

    // dispatch initialized event
    this.sendCustomEvent('rsq.service.googleadsgtag.initialized', {
      awCreate
    })
  }

  write() {
    // update consent accordingly
    gtag('consent', 'update', this.buildConsentModeUpdate('granted'))

    // trigger callback if necessary
    if (typeof this.gtagMore === 'function') {
      this.gtagMore()
    }

    // dispatch ready event
    this.sendCustomEvent('rsq.service.googleadsgtag.ready')

    // register event listeners
    this.addListeners()
  }

  fallback() {
    // update consent accordingly
    gtag('consent', 'update', this.buildConsentModeUpdate('denied'))

    // dispatch disabled event
    this.sendCustomEvent('rsq.service.googleadsgtag.disabled')
  }

  addListeners() {
    if (this.trackFormConversions) {
      document.addEventListener('wpcf7mailsent', async (event) => {
        let currentCfForm = ''
        if (typeof event.detail.apiResponse !== 'undefined' && typeof event.detail.apiResponse.into !== 'undefined') {
          currentCfForm = event.detail.apiResponse.into.replace('#', '')
          const targetedCfForm = document.getElementById(currentCfForm).querySelector('.wpcf7-form').name
          if (typeof targetedCfForm !== 'undefined') {
            currentCfForm = targetedCfForm
          }

          const conversionData = this.formConversions[currentCfForm] || false
          if (conversionData) {
            const uniqueId = this.getUniqueIdentifier()
            const conversionLabel = conversionData.conversion || false
            if (conversionLabel) {
              const userData = {}
              const fields = conversionData.fields || {}
              if (Object.keys(fields).length > 0) {
                for (const fieldName in fields) {
                  let mappedFieldName = fields[fieldName]
                  const fieldEl = document.querySelector(`[name="${fieldName}"]`)
                  if (fieldEl) {
                    let value = fieldEl.value
                    switch (mappedFieldName) {
                      case 'phone_number':
                        // convert phone number for international format
                        try {
                          const dynImport = await import('libphonenumber-js')
                          const parsePhoneNumber = await dynImport.default
                          const parsedPhoneNumber = parsePhoneNumber(value, {
                            defaultCountry: 'FR'
                          })

                          value = parsedPhoneNumber.format('E.164')
                        } catch (err) {
                          console.log('Failed to parse phone number with error', err)
                        }
                        break
                      default:
                        break
                    }

                    if (mappedFieldName.includes('address.')) {
                      mappedFieldName = mappedFieldName.replace('address.', '')
                      if (typeof userData.address === 'undefined') {
                        userData.address = {}
                      }

                      userData.address[mappedFieldName] = value
                    } else {
                      userData[mappedFieldName] = value
                    }
                  }
                }
              }

              // fill advanced conversion user data
              if (Object.keys(userData).length > 0) {
                gtag('set', 'user_data', userData)
              }

              // send conversion
              gtag('event', 'conversion', {
                send_to: `${uniqueId}/${conversionLabel}`
              })
            }
          }

          // recup des tracking de conversion du adwords.yaml
          /* const configCf = alphaConfig?.adwordsConversions?.cform
          const tracking = configCf && configCf[currentCfForm] ? configCf[currentCfForm] : false
          if (tracking) {
            // eslint-disable-next-line no-undef
            gtag('event', 'conversion', {
              // eslint-disable-next-line camelcase
              send_to: `${gadsAccountId}/${tracking.conversion}`
            })
          }*/
        }
      })
    }
  }
}
